import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Building2, Store, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const portalType = searchParams.get("portal"); // enterprise, partner, merchant
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);
  
  // Determine which API endpoint to use based on portal type
  const getResetEndpoint = () => {
    if (portalType === "enterprise" || portalType === "partner" || portalType === "merchant") {
      return "/b2b/reset-password";
    }
    return "/auth/reset-password";
  };
  
  // Determine redirect URL after successful reset
  const getRedirectUrl = () => {
    switch (portalType) {
      case "enterprise":
        return "/enterprise";
      case "partner":
        return "/partners";
      case "merchant":
        return "/"; // Merchants need their specific portal URL
      default:
        return "/auth";
    }
  };
  
  // Get portal display info
  const getPortalInfo = () => {
    switch (portalType) {
      case "enterprise":
        return { icon: Building2, name: "Enterprise Portal", color: "text-blue-400" };
      case "partner":
        return { icon: Users, name: "Partner Portal", color: "text-purple-400" };
      case "merchant":
        return { icon: Store, name: "Merchant Portal", color: "text-green-400" };
      default:
        return { icon: Lock, name: "Viraly", color: "text-viraly-orange" };
    }
  };
  
  const portalInfo = getPortalInfo();
  const PortalIcon = portalInfo.icon;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await apiClient.post(getResetEndpoint(), {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success("Password reset successful!");
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate(getRedirectUrl());
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password. Link may have expired.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-viraly-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className={`w-12 h-12 rounded-full ${portalType ? 'bg-white/10' : 'bg-viraly-orange/20'} flex items-center justify-center mx-auto mb-3`}>
            <PortalIcon className={`w-6 h-6 ${portalInfo.color}`} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-viraly-light">{portalInfo.name}</h1>
          <p className="text-viraly-muted mt-2">Reset Your Password</p>
        </div>
        
        <div className="card-base p-8">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Password Reset Successful!</h2>
              <p className="text-viraly-muted mb-4">
                Your password has been updated. Redirecting to login...
              </p>
              <Button onClick={() => navigate(getRedirectUrl())} className="btn-primary">
                Go to Login
              </Button>
            </motion.div>
          ) : error && !token ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Invalid Link</h2>
              <p className="text-viraly-muted mb-4">{error}</p>
              <Button onClick={() => navigate("/auth")} className="btn-primary">
                Back to Login
              </Button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-viraly-orange/20 flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-6 h-6 text-viraly-orange" />
                </div>
                <h2 className="font-heading text-xl font-bold">Create New Password</h2>
                <p className="text-sm text-viraly-muted mt-1">
                  Enter your new password below
                </p>
              </div>
              
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-base pr-10"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-viraly-muted hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-viraly-muted">Minimum 8 characters</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-base"
                  placeholder="Confirm new password"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="btn-primary w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting Password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
              
              <p className="text-center text-sm text-viraly-muted">
                Remember your password?{" "}
                <button
                  type="button"
                  onClick={() => navigate(getRedirectUrl())}
                  className="text-viraly-orange hover:underline"
                >
                  Back to Login
                </button>
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
